import { useEffect, useRef } from 'react'

import * as Sentry from '@sentry/react'

import { useChatRefs } from '@/contexts/ChatRefs/useChatRefs'
import { useApi } from '@/hooks/useApi'
import { useAllConversationsStore } from '@/store/useAllConversationsStore'
import { useCurrentConversationStore } from '@/store/useCurrentConversationStore'

import useChatScroll from './useChatScroll'

// Uncomment for debugging flicker / event ordering
// import { debugStreamingEvent } from './__debugStreaming'

const FLUSH_INTERVAL = 45 // ms – limit flushes to ~22 per second

/**
 * Handles the streaming of assistant messages to the chat.
 *
 * Streamdown already supports incremental markdown rendering efficiently.
 * The most stable approach is to keep `message.content` as the single source
 * of truth and update it as tokens arrive (throttled), rather than writing to
 * the DOM directly.
 *
 * @returns {Object}  streamAgentMessage  function to stream an assistant message
 */
export function useChatStreaming() {
  const api = useApi()
  const addStreamingAssistant = useCurrentConversationStore(
    s => s.addStreamingAssistant,
  )
  const patchAssistant = useCurrentConversationStore(s => s.patchAssistant)
  const setStreamPhase = useCurrentConversationStore(s => s.setStreamPhase)
  const setAgentStatus = useCurrentConversationStore(s => s.setAgentStatus)
  const patchLastAsError = useCurrentConversationStore(s => s.patchLastAsError)
  const messages = useCurrentConversationStore(s => s.messages)
  const loadConversations = useAllConversationsStore(s => s.loadConversations)

  // Refs for scrolling and streaming buffers
  const { lastAgentMessageRef } = useChatRefs()
  const { atBottomRef, autoScrollEnabledRef } = useChatScroll(messages)

  const activeClientIdRef = useRef(null)
  const streamedContentRef = useRef('') // full content so far
  const chunkBufferRef = useRef('') // recent token fragments waiting to flush

  const lastFlushTimeRef = useRef(0)
  const rafIdRef = useRef(0)

  const flushToStateRef = useRef(null)

  flushToStateRef.current = (timestamp, { force = false } = {}) => {
    rafIdRef.current = 0

    if (!force && timestamp - lastFlushTimeRef.current < FLUSH_INTERVAL) {
      rafIdRef.current = requestAnimationFrame(ts =>
        flushToStateRef.current?.(ts),
      )
      return
    }
    lastFlushTimeRef.current = timestamp

    const buffered = chunkBufferRef.current
    if (!buffered) return

    chunkBufferRef.current = ''
    streamedContentRef.current += buffered

    const clientId = activeClientIdRef.current
    if (clientId) {
      patchAssistant(clientId, { content: streamedContentRef.current })

      if (atBottomRef.current && autoScrollEnabledRef.current) {
        lastAgentMessageRef.current?.scrollIntoView({
          block: 'end',
          behavior: 'auto',
        })
      }
    }
  }

  const scheduleFlush = () => {
    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(ts =>
        flushToStateRef.current?.(ts),
      )
    }
  }

  const forceFlush = () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = 0
    }
    // performance.now() is safe in browsers; fallback for safety.
    const ts =
      typeof performance !== 'undefined' && performance.now
        ? performance.now()
        : Date.now()
    flushToStateRef.current?.(ts, { force: true })
  }

  const streamAgentMessage = async (streamUrl, streamOptions = {}) => {
    setStreamPhase('loading')

    const clientId = addStreamingAssistant()
    activeClientIdRef.current = clientId
    streamedContentRef.current = ''
    chunkBufferRef.current = ''
    lastFlushTimeRef.current = 0

    requestAnimationFrame(() => {
      lastAgentMessageRef.current?.scrollIntoView({ block: 'start' })
    })

    const currentSpan = Sentry.startInactiveSpan({ name: 'chatStreaming' })

    const controller = new AbortController()
    const cleanup = await api.streamAgentMessage(streamUrl, {
      signal: controller.signal,
      query: streamOptions.query,
      onMessage: (type, data) => {
        // debugStreamingEvent?.(type, data)
        switch (type) {
          case 'start':
            setAgentStatus('idle')
            setStreamPhase('streaming')
            currentSpan?.addEvent('start', { eventName: 'start' })
            break

          case 'agent-status':
            setAgentStatus(data || 'Cooking up some copy...')
            currentSpan?.addEvent('agent-status', { data })
            break

          case 'token-fragment':
            if (typeof data === 'string' && data.length) {
              chunkBufferRef.current += data
              scheduleFlush()
            }
            break

          case 'token-block':
            // token-block indicates paragraph boundaries. Our backend sends both
            // token-fragment and token-block events.
            //
            // Important: token-block contains *already emitted* content. If we
            // treated it as new content we'd duplicate output. So we only use it
            // as a timing hint to flush quickly.
            currentSpan?.addEvent('token-block', { data })
            forceFlush()
            break

          case 'assistant':
            // Ensure we apply any last buffered fragments before final patch.
            forceFlush()
            currentSpan?.addEvent('assistant', { data })
            patchAssistant(clientId, data)
            // reset refs for this stream
            activeClientIdRef.current = null
            streamedContentRef.current = ''
            chunkBufferRef.current = ''
            break

          case 'done':
            // Server indicates stream completion.
            forceFlush()
            setStreamPhase('idle')
            setAgentStatus('idle')
            currentSpan?.addEvent('done', { eventName: 'done' })
            currentSpan?.end()
            break

          case 'error':
            forceFlush()
            setStreamPhase('idle')
            setAgentStatus('idle')
            currentSpan?.addEvent('error', {
              eventName: 'error',
              data: {
                error: data || 'Sorry, there was an error getting a response.',
              },
            })
            currentSpan?.end()
            patchLastAsError('Sorry, there was an error getting a response.')
            break

          default:
            currentSpan?.addEvent(`${type}`, { eventName: `${type}`, data })
            break
        }
      },
    })

    loadConversations(api)

    return () => {
      controller.abort()
      cleanup?.()
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = 0
      activeClientIdRef.current = null
      streamedContentRef.current = ''
      chunkBufferRef.current = ''
      setStreamPhase('idle')
      setAgentStatus('idle')
      currentSpan?.end()
    }
  }

  useEffect(() => {
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
    }
  }, [])

  return { streamAgentMessage }
}
